import React, { Dispatch, SetStateAction } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Typography, SelectionGroup, SelectionGroupItem } from 'fronton-react';
import { useFormContext } from 'react-hook-form';
import { getAreaState } from 'pages/common/helpers/get-area';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { TRoomsParams } from 'common/types/floor-params.type';
import { LayingTypeLabel } from 'pages/common/components/LayingTypeField/const';
import { activeRoomId, calculatedRoomsParams, roomArea, roomsAtom } from 'pages/common/store/rooms';
import { ParametersEnum, TInputParams } from 'pages/common/types';
import { getRoomTypeTitle } from 'pages/common/helpers/get-room-type-title';
import { gaClickRoom } from 'ga-events/ga-click-room';
import { getPathname } from 'utils/get-pathname';
import { getSelectionGroupSize, getFormattedSquare, getClearedRoomParams } from './helpers';
import {
  RoomsParamsContainer,
  CalculatedRoomsContainer,
  SelectionGroupClassname,
  RoomParametersWrapper,
  SelectionGroupItemClassname,
} from './styles';
import { ANY_ROOM_TYPE } from '../../consts/rooms';
import { TAB_LIST } from '../../consts/dimensions-tab-list';

type TProps = {
  setInputParams: Dispatch<SetStateAction<TInputParams>>;
  setActiveTabIndex: Dispatch<SetStateAction<number>>;
  layingTypeFieldName?: string;
  defaultInputParams: TInputParams;
  isCalculator: boolean;
};

export const Rooms = ({
  setInputParams,
  setActiveTabIndex,
  layingTypeFieldName = 'thickness',
  defaultInputParams,
  isCalculator,
}: TProps) => {
  const { setValue, clearErrors } = useFormContext();
  const { mobile } = useBreakpoints() ?? {};
  const [selectedRoomId, setSelectedRoomId] = useRecoilState(activeRoomId);
  const [, setIsAreaEnabled] = useRecoilState(roomArea);
  const calculatedRooms = useRecoilValue(roomsAtom);
  const [, setCalculatedRoomParams] = useRecoilState<TRoomsParams>(calculatedRoomsParams);

  const changeInputsParams = (isEnabled: boolean) => {
    const areaState = getAreaState({
      inputParams: defaultInputParams,
      isEnabled,
    });
    setInputParams(areaState);
  };

  const handleClickRoomParamsBtn = (room: TRoomsParams) => {
    const { isCalculatedRoomsParamsHasOnlySquare, width, length, square } = getClearedRoomParams(room);
    const formattedSquare = getFormattedSquare(room);
    const roomTypeTitle = getRoomTypeTitle(room);
    const path = getPathname();
    setValue(ParametersEnum.width, width);
    setValue(ParametersEnum.length, length);
    setValue(ParametersEnum.area, square);
    if (room.typeInstallation) {
      setValue(layingTypeFieldName, room.typeInstallation);
    }
    setValue('roomType', room.roomType ?? ANY_ROOM_TYPE);
    if (width === '' && length === '') {
      setActiveTabIndex(TAB_LIST.AREA);
    } else {
      setActiveTabIndex(TAB_LIST.LENGTH_WIDTH);
    }
    setCalculatedRoomParams(room);
    changeInputsParams(isCalculatedRoomsParamsHasOnlySquare);
    setIsAreaEnabled(isCalculatedRoomsParamsHasOnlySquare);
    setSelectedRoomId(room.id);
    clearErrors([ParametersEnum.width, ParametersEnum.length, ParametersEnum.area]);
    gaClickRoom({
      elementBlock: isCalculator ? 'productDetail / calc' : `${path}:lvl3`,
      elementName: isCalculator ? 'Ввод размеров' : 'Проекты PLP / Подбор ламината',
      elementText: `${roomTypeTitle} ${formattedSquare} м²`,
      elementVariant: isCalculatedRoomsParamsHasOnlySquare ? 'Площадь' : 'Размеры',
    });
  };

  return (
    <RoomsParamsContainer>
      <CalculatedRoomsContainer data-testid="calculated-rooms-container">
        <SelectionGroup
          active={selectedRoomId}
          onChange={() => {
            // This is intentional
          }}
          variant="composite"
          size={getSelectionGroupSize(mobile)}
          className={SelectionGroupClassname}>
          {calculatedRooms.map((room, i) => {
            const formattedSquare = getFormattedSquare(room);
            const roomTypeTitle = getRoomTypeTitle(room);

            return (
              <SelectionGroupItem
                data-testid={`button-with-room-params-${i}`}
                key={room.id}
                value={room.id}
                onClick={() => handleClickRoomParamsBtn(room)}
                className={SelectionGroupItemClassname}>
                <RoomParametersWrapper>
                  <Typography size="body_accent" variant="m">
                    {roomTypeTitle} {formattedSquare} м²
                  </Typography>
                  <Typography size="body_long" variant="s" color="text-minor">
                    {LayingTypeLabel[room.typeInstallation as 'horizontal' | 'diagonal']}
                  </Typography>
                </RoomParametersWrapper>
              </SelectionGroupItem>
            );
          })}
        </SelectionGroup>
      </CalculatedRoomsContainer>
    </RoomsParamsContainer>
  );
};
